import { DraggableMultiContainerList } from '@amelio/ui-library';

import PriorityOption from '../PriorityOption/PriorityOption';
import {
    PriorityContainer,
    type PriorityContainerConfig,
} from './components/PriorityContainer/PriorityContainer';

import type { PriorityAttribute } from '@/types/priorityAttributeType';
import type {
    DraggableContainer,
    DraggableContainerMetadata,
    DraggableMultiContainerItem,
} from '@amelio/ui-library';
import type { ReactNode } from 'react';

const PRIORITY_1_CONTAINER_ID = 'priority-1';
const PRIORITY_2_CONTAINER_ID = 'priority-2';
const PRIORITY_3_CONTAINER_ID = 'priority-3';

const ITEM_GAP_PX = 16;
const CONTAINER_GAP_PX = 40;

// Unlayered utility, never Tailwind variants: the library sheet's base rule is emitted after ours
// and would win at every width.
const RESPONSIVE_GRID_CLASS = 'responsive-grid-1-3-xl';

interface PriorityBoardContainerConfig extends PriorityContainerConfig {
    items: DraggableMultiContainerItem<PriorityAttribute>[];
}

interface PriorityBoardProps {
    containers: PriorityBoardContainerConfig[];
    onChange: (containers: DraggableContainer<PriorityAttribute>[]) => void;
    onAddAttribute?: (containerId: string) => void;
    onRemoveItem?: (containerId: string, itemId: string) => void;
    getRemoveLabel?: (attribute: PriorityAttribute) => string;
    disabled?: boolean;
    dataTestId?: string;
    className?: string;
}

const PriorityBoard = ({
    containers: containerConfigs,
    onChange,
    onAddAttribute,
    onRemoveItem,
    getRemoveLabel,
    disabled = false,
    dataTestId = 'priority-board',
    className = '',
}: PriorityBoardProps) => {
    const configMap = new Map<string, PriorityBoardContainerConfig>(
        containerConfigs.map((config) => [config.id, config]),
    );

    const containers: DraggableContainer<PriorityAttribute>[] = containerConfigs.map((config) => ({
        id: config.id,
        items: config.items,
    }));

    const handleAddClick = (containerId: string) => {
        onAddAttribute?.(containerId);
    };

    const findContainerIdOf = (itemId: string): string =>
        containerConfigs.find((config) => config.items.some((item) => item.id === itemId))?.id ??
        '';

    const renderContainer = (
        container: DraggableContainer<PriorityAttribute>,
        metadata: DraggableContainerMetadata,
        children: ReactNode,
    ) => {
        const config = configMap.get(container.id);

        if (!config) {
            return <div>{children}</div>;
        }

        return (
            <PriorityContainer
                config={config}
                isOver={metadata.isOver}
                disabled={disabled}
                dataTestId={dataTestId}
                onAddClick={handleAddClick}
            >
                {children}
            </PriorityContainer>
        );
    };

    return (
        <DraggableMultiContainerList
            containers={containers}
            onChange={onChange}
            renderItem={(item, metadata) => (
                <PriorityOption
                    attribute={item.data}
                    isDragging={metadata.isDragging}
                    dataTestId={`${dataTestId}-item-${item.id}`}
                    onRemove={
                        onRemoveItem
                            ? () => onRemoveItem(findContainerIdOf(item.id), item.id)
                            : undefined
                    }
                    removeLabel={getRemoveLabel?.(item.data)}
                />
            )}
            renderContainer={renderContainer}
            disabled={disabled}
            dataTestId={dataTestId}
            containerGap={CONTAINER_GAP_PX}
            itemGap={ITEM_GAP_PX}
            className={`grid! w-full ${RESPONSIVE_GRID_CLASS} *:h-full ${className}`}
        />
    );
};

export { PriorityBoard, PRIORITY_1_CONTAINER_ID, PRIORITY_2_CONTAINER_ID, PRIORITY_3_CONTAINER_ID };
export type { PriorityBoardProps, PriorityBoardContainerConfig };
